/**
 * Owner: Masa Bayu
 * File: classify-page.ts
 * Date:Time: 2026-10-09 19:00
 * Description: Fasa 13+17+21 · classify · text-plate · table/list slots empty.
 */

import { detectTextPlates } from './text-plate-detector';
import type { FlatAtom, StructuredPageData } from './types';

/** Single-letter / short dropcap glyph · large size must not become header. */
function isDropcapAtom(
  atom: FlatAtom,
  modeFontSize: number,
  headerThreshold: number,
): boolean {
  const text = (atom.text ?? '').trim();
  if (!text || text.length > 3) return false;
  const fs = atom.fontSize || modeFontSize;
  if (fs < headerThreshold) return false;
  return atom.width < modeFontSize * 2;
}

export function classifyPage(atoms: FlatAtom[], pageHeight: number): StructuredPageData {
  const visualLayer: FlatAtom[] = [];
  const footerLayer: FlatAtom[] = [];
  const headerLayer: FlatAtom[] = [];
  const bodyLayer: FlatAtom[] = [];

  // PERINGKAT 1: Visual Isolation (Keutamaan Mutlak)
  const nonVisualAtoms = atoms.filter((atom) => {
    if (atom.type === 'image' || atom.type === 'vector') {
      visualLayer.push(atom);
      return false;
    }
    return true;
  });

  // PERINGKAT 1b: Fasa 17 · semantic text-plate dedupe (Raster39-class)
  const { suppressImageIds } = detectTextPlates(atoms);
  const suppress = new Set(suppressImageIds);
  const filteredVisualLayer = visualLayer.filter((img) => !suppress.has(img.id));

  // PERINGKAT 2: Footer Detection (Geometri Relatif)
  const contentAtoms = nonVisualAtoms.filter((atom) => {
    if (atom.y > pageHeight * 0.9) {
      footerLayer.push(atom);
      return false;
    }
    return true;
  });

  // PERINGKAT 3: Statistical Typography (Header vs Body)
  if (contentAtoms.length > 0) {
    const fontSizeCounts = new Map<number, number>();

    contentAtoms.forEach((atom) => {
      const fs = atom.fontSize || 11;
      fontSizeCounts.set(fs, (fontSizeCounts.get(fs) || 0) + 1);
    });

    let modeFontSize = 11;
    let maxCount = 0;
    fontSizeCounts.forEach((count, fs) => {
      if (count > maxCount) {
        maxCount = count;
        modeFontSize = fs;
      }
    });

    const headerThreshold = modeFontSize * 1.5;

    contentAtoms.forEach((atom) => {
      const fs = atom.fontSize || modeFontSize;
      // Dropcap glyph stays in body for Knuth / Face dropcap path.
      if (isDropcapAtom(atom, modeFontSize, headerThreshold)) {
        bodyLayer.push(atom);
      } else if (fs >= headerThreshold) {
        headerLayer.push(atom);
      } else {
        bodyLayer.push(atom);
      }
    });
  }

  /*
   * tableLayer / listLayer · required StructuredPageData slots for UE Align/Lexical.
   * Tip classifyPage does not assign specialized membership (no heuristic push).
   * Empty arrays = lawful continuum · fields present · assignment not invented here.
   */
  const tableLayer: FlatAtom[] = [];
  const listLayer: FlatAtom[] = [];

  return {
    visualLayer: filteredVisualLayer,
    headerLayer,
    bodyLayer,
    footerLayer,
    tableLayer,
    listLayer,
  };
}
